// ============================================================
// useBillingLive.ts — live billing screens, no refresh.
//
// Rides the EXISTING presence WebSocket (services/presence.ts — the socket the
// live case sections use); no second connection. Three inputs, coalesced into
// one callback per burst (a run of line edits → one refetch):
//   • 'bill-changed' — "bill X on case Y changed (kind)". The server only sends
//     it to users allowed to see that case's bills (lib/presence.ts
//     emitBillChanged → billingAccess.billsVisibleOnCase), and it carries no
//     figures — consumers re-read through the normal guarded REST endpoints.
//   • reconnect — events sent while the socket was down are gone, so consumers
//     refetch current state (resync = true).
//   • permissions-changed — "view all billing" / seeAllCases may have changed
//     what this user sees (resync = true).
// Optional `onCaseAudience`: a case's owner / team / firm-wide changed (the
// existing entity-changed channel). The consumer says whether that matters to
// what it shows (e.g. a restricted user just gained / lost the case) → resync.
// ============================================================
import { useEffect, useRef } from 'react'
import { presence, type PresenceBillChanged } from '../services/presence'
import { useCasevoStore } from '../store/useCasevoStore'

export interface CaseAudienceChange { caseId: string; inAudience: boolean; involved: boolean }

export const useBillingLive = (
  onSignal: (events: PresenceBillChanged[], resync: boolean) => void,
  opts: { enabled?: boolean; onCaseAudience?: (c: CaseAudienceChange) => boolean; delayMs?: number } = {},
): void => {
  const sigRef = useRef(onSignal)
  sigRef.current = onSignal
  const audRef = useRef(opts.onCaseAudience)
  audRef.current = opts.onCaseAudience
  const enabled = opts.enabled !== false
  const delay = opts.delayMs ?? 150
  const me = useRef<string | null>(null)
  me.current = useCasevoStore((st) => st.authUser?.id ?? null)

  useEffect(() => {
    if (!enabled) return
    let timer: ReturnType<typeof setTimeout> | null = null
    let pending: PresenceBillChanged[] = []
    let resync = false
    const flush = (): void => {
      timer = null
      const evs = pending; const full = resync
      pending = []; resync = false
      sigRef.current(evs, full)
    }
    const queue = (e: PresenceBillChanged | null): void => {
      if (e) pending.push(e); else resync = true
      if (!timer) timer = setTimeout(flush, delay)
    }
    const offs = [
      presence.subscribeBillChanged((e) => queue(e)),
      presence.subscribeReconnected(() => queue(null)),
      presence.subscribePermissionsChanged(() => queue(null)),
      presence.subscribeEntityChanged((e) => {
        if (e.entity !== 'case' || e.op !== 'update' || !audRef.current) return
        const s = (e.summary ?? {}) as Record<string, unknown>
        if (!Array.isArray(s.assignees) || !('assignedToUserId' in s)) return // not an audience-bearing update
        const uid = me.current
        if (!uid) return
        const team = (s.assignees as { userId: string }[]).map((a) => a.userId)
        const involved = s.assignedToUserId === uid || team.includes(uid)
        if (audRef.current({ caseId: e.id, inAudience: involved || s.firmWide === true, involved })) queue(null)
      }),
    ]
    return () => { offs.forEach((off) => off()); if (timer) clearTimeout(timer) }
  }, [enabled, delay])
}
